/**
 * AudioManager - Comprehensive audio system for PokerBlitz
 *
 * Features:
 * - Dynamic music that changes based on gameplay intensity
 * - Contextual sound effects for all game actions
 * - Audio settings with granular control
 * - Performance optimized for mobile devices
 */

import * as Sentry from '@sentry/capacitor';
import { Capacitor } from '@capacitor/core';

export interface AudioSettings {
  masterVolume: number; // 0-1
  musicVolume: number; // 0-1
  sfxVolume: number; // 0-1
  musicEnabled: boolean;
  sfxEnabled: boolean;
}

export type SoundEffect =
  | 'card-flip'
  | 'card-select'
  | 'card-deselect'
  | 'card-deal'
  | 'hand-complete'
  | 'hand-complete-big' // 5×+ multiplier tier
  | 'hand-complete-royal' // royal / straight flush tier
  | 'streak-start'
  | 'streak-increase'
  | 'streak-activate' // Alias for streak-start
  | 'streak-decay-warning' // last 5s of streak timer
  | 'streak-break' // streak resets to 1×
  | 'golden-card-spawn' // new golden card lands on board
  | 'golden-card-played' // hand with golden card submitted
  | 'locked-card-unlock' // locked countdown hits 0
  | 'achievement-unlock'
  | 'level-up'
  | 'game-start'
  | 'game-end'
  | 'button-click'
  | 'button-hover'
  | 'error'
  | 'success'
  | 'powerup-activate'
  | 'powerup-use' // Alias for powerup-activate
  | 'daily-challenge-complete'
  | 'timer-warning'
  | 'invalid';

export type SoundPack = 
  | 'classic'
  | 'vegas'
  | 'modern'
  | 'royal'
  | 'ocean'
  | 'sunset'
  | 'midnight'
  | '8bit'
  | 'western'
  | 'forest'
  | 't800'
  | 'vegas_plus'
  | 'spooky-halloween';

/**
 * Music tracks whose MP3s stay inside the native binary after
 * scripts/prepare-native-build.js prunes dist/audio/music (StackedStreak-ns2j).
 * Everything else is streamed from REMOTE_MUSIC_ORIGIN on first use.
 *
 * KEEP IN SYNC with KEEP_MUSIC_FILES in scripts/prepare-native-build.js —
 * src/lib/audio/__tests__/AudioManager.native-music.test.ts enforces the pairing.
 */
export const NATIVE_BUNDLED_MUSIC_TRACKS = ['menu', 'classic-casino'] as const;

export type MusicTrack =
  | 'menu'
  | 'halloween-menu'
  | 'classic-casino'
  | 'vegas-nights'
  | 'modern-dark'
  | 'royal-gold'
  | 'ocean-blue'
  | 'sunset-glow'
  | 'midnight-sky'
  | '8bit-retro'
  | 'wild-west-saloon'
  | 'forest-zen'
  | 'robots-are-coming'
  | 'vegas-plus-neon-palace'
  | 'spooky-halloween';

class AudioManager {
  // Native binaries only bundle the tracks in NATIVE_BUNDLED_MUSIC_TRACKS
  // (menu + default/classic theme, for offline first-run). The rest of the
  // catalog is fetched over HTTPS from the web deploy on first use and held
  // in the existing musicBufferCache LRU (StackedStreak-ns2j — this took the
  // APK/IPA payload from ~45-47 MB down to ~13 MB). Web builds are
  // unaffected: they always serve /audio/music/* from their own origin.
  private static readonly NATIVE_BUNDLED_MUSIC: ReadonlySet<string> =
    new Set(NATIVE_BUNDLED_MUSIC_TRACKS);
  private static readonly REMOTE_MUSIC_ORIGIN = 'https://pokerblitzgame.com';

  // SFX that only the classic pack ships on disk (bd gw43): the 9 newer
  // effects (hand-complete tiers, golden-card stingers, timer-warning, …)
  // were only ever produced for classic/. Requesting them under another
  // pack's path guaranteed a 404 (web) or fetch reject (native) before
  // loadClassicFallback recovered — /audio/sfx/<pack>/timer-warning.mp3
  // 404'd on every themed game (ui-audit-cross perf report). For these
  // sounds on non-classic packs we skip the doomed request and load the
  // classic asset directly. Admin sound overrides still take precedence.
  private static readonly CLASSIC_ONLY_SFX: ReadonlySet<SoundEffect> = new Set<SoundEffect>([
    'card-deal',
    'golden-card-spawn',
    'golden-card-played',
    'hand-complete-big',
    'hand-complete-royal',
    'level-up',
    'locked-card-unlock',
    'streak-break',
    'streak-decay-warning',
    'timer-warning',
  ]);

  private audioContext: AudioContext | null = null;
  private settings: AudioSettings;

  // Music plays through Web Audio API (gapless loop) rather than HTMLAudioElement
  // to avoid the iOS WKWebView 200-500ms silence gap at track loop points.
  // The source is disposable (AudioBufferSourceNode can only be started once),
  // so we keep the node + its gain around and rebuild on track change.
  private musicSource: AudioBufferSourceNode | null = null;
  private musicGain: GainNode | null = null;
  // Bounded LRU on decoded music buffers (perf brief CODE-08 / bd dmpv).
  // Pre-fix this Map grew unbounded as the user switched themes — up to
  // 15 tracks × ~3-5 MB decoded PCM = ~75 MB of heap held forever, the
  // most concrete contributor to bd rq6's iPad watchdog termination.
  // Keep at most MUSIC_CACHE_MAX entries; on insert past the cap, evict
  // the oldest non-current entry (Map iteration preserves insertion order).
  private static readonly MUSIC_CACHE_MAX = 2;
  private musicBufferCache = new Map<MusicTrack, AudioBuffer>();
  private musicLoadInFlight = new Map<MusicTrack, Promise<AudioBuffer | null>>();
  private isMusicPaused = false;
  private pausedTrack: MusicTrack | null = null;
  // HTMLAudioElement fallback for when decodeAudioData rejects the bundled MP3.
  // (StackedStreak-i0b0) iOS 26.3 / Mac Catalyst WebKit's strict Web Audio
  // decoder rejects the same MP3 files the native <audio> decoder plays fine.
  // Pre-eef05128 the whole music path was HTMLAudioElement; we now use it as a
  // fallback so devices/runtimes where decode fails still get music (with the
  // 200-500ms WKWebView loop gap) instead of silence.
  private musicAudio: HTMLAudioElement | null = null;
  // Tracks where the gapless Web Audio path has already failed to decode in
  // this session — short-circuit to the HTMLAudioElement fallback on retries
  // so we don't re-fetch + re-decode every playMusic() call.
  private musicDecodeFailed = new Set<MusicTrack>();
  // setInterval handle for fadeMusic on the HTMLAudioElement fallback path
  // (no GainNode to ramp there). Cleared in stopMusic()/playMusicFallback()
  // so overlapping fades can't fight over the element's volume.
  private musicFadeInterval: ReturnType<typeof setInterval> | null = null;
  private currentTrack: MusicTrack | null = null;
  private isInitialized = false;
  private currentSoundPack: SoundPack = 'classic';
  // Debounce handle for the onstatechange interruption-recovery path —
  // CallKit banners / Siri can flap the context state several times in
  // quick succession; we only want one resume attempt per interruption.
  private interruptionResumeTimer: ReturnType<typeof setTimeout> | null = null;

  // Sound effect cache - organized by sound pack
  private soundCache = new Map<string, AudioBuffer>();

  // Admin-configured overrides: sound-type (dash format) → signed URL.
  // Set via setSoundOverrides() when the theme loads; takes precedence over
  // the static /audio/sfx/{pack}/{sound}.mp3 files.
  private soundOverrides: Map<string, string> = new Map();

  // Admin-configured music overrides: MusicTrack → signed URL.
  // Set via setMusicTrackOverride() when the theme loads; takes precedence
  // over the static /audio/music/*.mp3 files.
  private musicOverrides: Map<string, string> = new Map();
  
  // Music tracks configuration
  private musicTracks: Record<MusicTrack, string> = {
    'menu': '/audio/music/menu-theme.mp3',
    'halloween-menu': '/audio/music/halloween-menu.mp3',
    'classic-casino': '/audio/music/classic-casino.mp3',
    'vegas-nights': '/audio/music/vegas-nights.mp3',
    'modern-dark': '/audio/music/modern-dark.mp3',
    'royal-gold': '/audio/music/royal-gold.mp3',
    'ocean-blue': '/audio/music/ocean-blue.mp3',
    'sunset-glow': '/audio/music/sunset-glow.mp3',
    'midnight-sky': '/audio/music/midnight-sky.mp3',
    '8bit-retro': '/audio/music/8-bit-retro.mp3',
    'wild-west-saloon': '/audio/music/wild-west-saloon.mp3',
    'forest-zen': '/audio/music/forest-zen.mp3',
    'robots-are-coming': '/audio/music/robots-are-coming.mp3',
    'vegas-plus-neon-palace': '/audio/music/vegas-plus-neon-palace.mp3',
    'spooky-halloween': '/audio/music/spooky-halloween.mp3'
  };

  // Theme to sound pack mapping
  private themeToSoundPack: Record<string, SoundPack> = {
    'classic': 'classic',
    'vegas': 'vegas',
    'modern': 'modern',
    'royal': 'royal',
    'ocean': 'ocean',
    'sunset': 'sunset',
    'midnight': 'midnight',
    '8bit': '8bit',
    'western': 'western',
    'forest': 'forest',
    't800': 't800',
    'vegas_plus': 'vegas_plus',
    'spooky-halloween': 'spooky-halloween'
  };

  // Sound effects configuration - theme-specific paths
  private getSoundPath(sound: SoundEffect, soundPack: SoundPack): string {
    return `/audio/sfx/${soundPack}/${sound}.mp3`;
  }

  /**
   * Expose theme→soundPack mapping so the admin panel can resolve the right
   * static fallback path without duplicating the table.
   */
  getSoundPackForTheme(themeId: string): SoundPack {
    return this.themeToSoundPack[themeId] ?? 'classic';
  }

  /**
   * Register Supabase-backed sound overrides for the active theme.
   * Key = SoundEffect name (dash format, e.g. "card-flip").
   * Value = presigned URL valid for the next hour.
   * Called by ThemeContext after switchSoundPack(); pass an empty Map to clear.
   */
  setSoundOverrides(overrides: Map<string, string>): void {
    this.soundOverrides = overrides;
    // Evict cached entries that now have an override so they're re-fetched
    for (const soundType of overrides.keys()) {
      this.soundCache.delete(`${this.currentSoundPack}-${soundType}`);
    }
  }

  /** Map a theme ID to its MusicTrack key (same mapping used during gameplay). */
  getMusicTrackForTheme(themeId: string): MusicTrack {
    const map: Record<string, MusicTrack> = {
      'classic': 'classic-casino',
      'vegas': 'vegas-nights',
      'modern': 'modern-dark',
      'royal': 'royal-gold',
      'ocean': 'ocean-blue',
      'sunset': 'sunset-glow',
      'midnight': 'midnight-sky',
      '8bit': '8bit-retro',
      'western': 'wild-west-saloon',
      'forest': 'forest-zen',
      't800': 'robots-are-coming',
      'vegas_plus': 'vegas-plus-neon-palace',
      'spooky-halloween': 'spooky-halloween',
    };
    return map[themeId] ?? 'classic-casino';
  }

  /** Return the static path for a MusicTrack, for display purposes. */
  getMusicTrackPath(track: MusicTrack): string {
    return this.musicTracks[track] ?? '';
  }

  /**
   * Resolve the non-override URL for a music track (StackedStreak-ns2j).
   * - Web builds: always the bundled /audio/music/*.mp3 path (unchanged).
   * - Native builds: the bundled path only for NATIVE_BUNDLED_MUSIC; every
   *   other track's MP3 was pruned from the binary by
   *   scripts/prepare-native-build.js and streams from the web deploy.
   */
  private resolveMusicUrl(track: MusicTrack): { url: string; source: 'static' | 'remote' } | null {
    const staticPath = this.musicTracks[track];
    if (!staticPath) return null;
    if (Capacitor.isNativePlatform() && !AudioManager.NATIVE_BUNDLED_MUSIC.has(track)) {
      return { url: `${AudioManager.REMOTE_MUSIC_ORIGIN}${staticPath}`, source: 'remote' };
    }
    return { url: staticPath, source: 'static' };
  }

  /**
   * Bundled track to play when a remote (CDN) track can't be fetched on a
   * native build — offline, captive portal, flaky network. Menu-flavored
   * tracks drop to the bundled menu theme; gameplay tracks drop to the
   * classic-casino theme. Both are always inside the binary.
   */
  private getOfflineFallbackTrack(track: MusicTrack): MusicTrack {
    return track === 'menu' || track === 'halloween-menu' ? 'menu' : 'classic-casino';
  }

  /**
   * Register a Supabase-backed music override for a specific track.
   * Call with a presigned URL (valid ~1 h); the buffer cache is evicted so
   * the next playMusic() call fetches the new file.
   * Pass url = '' or call clearMusicTrackOverride() to revert to static file.
   */
  setMusicTrackOverride(track: MusicTrack, url: string): void {
    if (url) {
      this.musicOverrides.set(track, url);
    } else {
      this.musicOverrides.delete(track);
    }
    this.musicBufferCache.delete(track);
    this.musicLoadInFlight.delete(track);
    // If this track is currently playing, restart it with the new URL
    if (this.currentTrack === track) {
      this.playMusic(track).catch(() => {});
    }
  }

  clearMusicTrackOverride(track: MusicTrack): void {
    this.musicOverrides.delete(track);
    this.musicBufferCache.delete(track);
    this.musicLoadInFlight.delete(track);
  }

  constructor() {
    // musicVolume bumped 0.3 → 0.5 (matches GameSettingsSection's stated default
    // of 50%). At the prior 0.3 with master=0.5 the effective gain was 0.15 —
    // technically audible but perceptually silent on most devices, especially
    // when card-flip SFX (effective 0.30, sharp transient) plays over it.
    // Multiple users (incl. dev on TestFlight 122) reported "music isn't
    // playing" when it was actually just below the noise floor.
    this.settings = {
      masterVolume: 0.5, // Reduced from 0.7 to play more passively alongside other audio
      musicVolume: 0.5,
      sfxVolume: 0.6, // Reduced from 0.8 for more subtle sound effects
      musicEnabled: true,
      sfxEnabled: true
    };
  }

  /**
   * Initialize audio system
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Create audio context with reduced latency and mix with other audio
      // This helps the audio play alongside other apps like Spotify
      this.audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)({
        latencyHint: 'interactive',
        sampleRate: 44100
      });

      // Load settings from localStorage
      this.loadSettings();

      // Interruption recovery: a CallKit banner or Siri overlay can suspend
      // the AudioContext WITHOUT a visibilitychange (document stays visible),
      // so the visibilitychange/pageshow handlers in page.tsx/App.tsx never
      // fire and music stays dead mid-game. When the context drops to
      // 'suspended'/'interrupted' while a track is active and the page is
      // still visible, attempt a debounced resume. Backgrounding is excluded
      // (visibilityState !== 'visible') so this never fights the existing
      // pause-on-hide behavior.
      this.audioContext.onstatechange = () => {
        // iOS reports 'interrupted', which isn't in the TS AudioContextState union.
        const state = this.audioContext?.state as string | undefined;
        if (
          (state === 'suspended' || state === 'interrupted') &&
          this.currentTrack &&
          document.visibilityState === 'visible'
        ) {
          if (this.interruptionResumeTimer !== null) {
            clearTimeout(this.interruptionResumeTimer);
          }
          this.interruptionResumeTimer = setTimeout(() => {
            this.interruptionResumeTimer = null;
            const current = this.audioContext?.state as string | undefined;
            if (
              (current === 'suspended' || current === 'interrupted') &&
              document.visibilityState === 'visible'
            ) {
              this.audioContext?.resume()
                .then(() => this.resumeMusic())
                .catch(() => {});
            }
          }, 400);
        }
      };

      // NOTE: preloadSounds() is intentionally NOT awaited here (perf
      // brief LOAD-10 / bd iqzy). It used to run at module-load before
      // any user gesture, adding ~6 sequential network RTTs to the
      // cold-start critical path even though iOS keeps the AudioContext
      // suspended until the first user tap anyway — so the preload was
      // both blocking startup AND useless until unlock() ran. Now we
      // kick it off as fire-and-forget so first paint isn't gated on
      // the decode work, AND the unlock() path below explicitly awaits
      // it on first tap so playback doesn't race against the preload.
      void this.preloadSounds();

      this.isInitialized = true;
    } catch (error) {
      // Silent fail - audio is non-critical
    }
  }

  /**
   * Get the audio context
   */
  getAudioContext(): AudioContext | null {
    return this.audioContext;
  }

  /**
   * Manually unlock audio (for user interaction requirements).
   * Called on first user tap, when iOS allows AudioContext to actually
   * resume. Ensures the preload kicked off in initialize() has settled
   * before any user-triggered playback so playSound doesn't race
   * against an in-flight decode (perf brief LOAD-10 / bd iqzy).
   */
  async unlock(): Promise<void> {
    await this.initialize();
    // initialize() fires preloadSounds as fire-and-forget so it doesn't
    // block cold start. Here we await it so user-triggered playback
    // sees the cache populated. Calling preloadSounds twice is safe —
    // loadSoundPack skips cache hits.
    await this.preloadSounds();
  }

  /**
   * Load settings from localStorage. Runs a one-shot migration on first load
   * after the v2 default-volume bump so existing devices don't stay stuck on
   * the old 0.3 setting (which was perceptually silent).
   */
  private loadSettings(): void {
    const saved = localStorage.getItem('audioSettings');
    if (saved) {
      try {
        this.settings = { ...this.settings, ...JSON.parse(saved) };
      } catch (error) {
        console.warn('AudioManager: Failed to load settings, using defaults');
      }
    }
    this.runVolumeMigration();
  }

  /**
   * One-shot migration: when the prior default musicVolume (0.3) was bumped
   * to 0.5 in build 125, every existing device kept its saved 0.3 in
   * localStorage and continued to perceive music as silent. This bumps the
   * stored value to 0.5 ONCE, but only when it's exactly the prior default
   * — so anyone who deliberately moved their slider to a different value
   * (including 0.3 by intent) is left alone the second-or-later time they
   * launch.
   *
   * Marker key prevents re-running if the user later returns to 0.3
   * intentionally. Safe to remove once usage data confirms the migration
   * has reached >99% of active devices.
   */
  private runVolumeMigration(): void {
    const MARKER = 'audioSettings_v2_volume_migrated';
    if (typeof localStorage === 'undefined') return;
    try {
      if (localStorage.getItem(MARKER)) return; // already migrated
      // Only bump if the stored value matches the legacy default exactly.
      // Floating-point comparison is fine here — both values are written
      // by the same JSON.stringify pipeline, so 0.3 stays 0.3 byte-for-byte.
      if (this.settings.musicVolume === 0.3) {
        this.settings.musicVolume = 0.5;
        this.saveSettings();
      }
      localStorage.setItem(MARKER, '1');
    } catch {
      // ignore — non-critical, migration will retry next launch
    }
  }

  /**
   * Save settings to localStorage
   */
  private saveSettings(): void {
    localStorage.setItem('audioSettings', JSON.stringify(this.settings));
  }

  /**
   * Preload essential sound effects for current sound pack
   */
  private async preloadSounds(): Promise<void> {
    if (!this.audioContext) {
      return;
    }

    const essentialSounds: SoundEffect[] = [
      'card-select',
      'card-flip',
      'hand-complete',
      'button-click',
      'success',
      'error'
    ];

    await this.loadSoundPack(this.currentSoundPack, essentialSounds);
  }

  /**
   * Whether a fetch Response for an audio asset should be treated as a
   * success. Normally that's `response.ok`, but on iOS Capacitor builds the
   * WebViewAssetHandler sends a plain URLResponse (no HTTP status) for media
   * file extensions (.mp3/.wav/etc), so fetch resolves with status 0 and
   * ok === false even though the bytes ARE delivered and arrayBuffer() reads
   * fine. The music path has handled this since the "music isn't showing up
   * on the app" fix in fetchAndDecodeMusic; the SFX paths gated purely on
   * response.ok, so on the live iOS app EVERY sound effect (card taps, hand
   * completes, stingers) fell through to generatePlaceholderSound and played
   * as a 0.2s synthesized beep — on every pack including classic.
   *
   * Genuinely missing files are unaffected: WebViewAssetHandler calls
   * didFailWithError for nonexistent paths, so fetch REJECTS and lands in the
   * callers' catch branches rather than here.
   */
  private isAcceptableAudioResponse(response: Response): boolean {
    return response.ok || (response.status === 0 && Capacitor.getPlatform() === 'ios');
  }

  /**
   * Load sound pack for a specific theme
   */
  async loadSoundPack(soundPack: SoundPack, sounds?: SoundEffect[]): Promise<void> {
    if (!this.audioContext) return;

    const soundsToLoad = sounds || [
      'card-flip', 'card-select', 'card-deselect', 'hand-complete',
      'streak-start', 'streak-increase', 'achievement-unlock',
      'game-start', 'game-end', 'button-click', 'button-hover',
      'error', 'success', 'powerup-activate', 'daily-challenge-complete'
    ];

    // Load all sounds in PARALLEL (perf brief LOAD-11 / bd iqzy).
    // Pre-fix this was a serial for-of loop with `await fetch` →
    // `await arrayBuffer` → `await decodeAudioData` per sound, so 6
    // essential sounds = up to 6 sequential network RTTs on cold start
    // (cold-start audio readiness was ~6× slower than necessary).
    // Promise.all is safe here because each iteration writes to its
    // own cache key and the fallback is per-sound.
    await Promise.all(
      soundsToLoad.map(async (sound) => {
        const cacheKey = `${soundPack}-${sound}`;

        // Skip if already cached
        if (this.soundCache.has(cacheKey)) return;

        try {
          // Admin-configured override takes precedence over the static file.
          const overrideUrl = this.soundOverrides.get(sound);
          // Classic-only SFX (see CLASSIC_ONLY_SFX): non-classic packs never
          // ship these files, so requesting the pack path just produced a
          // guaranteed 404 before the classic fallback kicked in. Go straight
          // to the classic asset instead.
          const effectivePack =
            !overrideUrl && soundPack !== 'classic' && AudioManager.CLASSIC_ONLY_SFX.has(sound)
              ? 'classic'
              : soundPack;
          const soundPath = overrideUrl ?? this.getSoundPath(sound, effectivePack);
          const response = await fetch(soundPath);

          if (this.isAcceptableAudioResponse(response)) {
            const arrayBuffer = await response.arrayBuffer();
            const audioBuffer = await this.audioContext!.decodeAudioData(arrayBuffer);
            this.soundCache.set(cacheKey, audioBuffer);
          } else {
            // File missing for this pack (bd gw43): only classic/ ships the
            // 7 newer SFX (hand-complete-big, golden-card-*, streak-break,
            // …), so non-classic packs used to land here and substitute a
            // synthesized beep for the biggest moments. Retry once with the
            // classic pack's real asset; the placeholder tone is last resort.
            const fallbackBuffer = await this.loadClassicFallback(sound, soundPack);
            this.soundCache.set(
              cacheKey,
              fallbackBuffer ?? this.generatePlaceholderSound(sound, soundPack),
            );
          }
        } catch (_error) {
          const fallbackBuffer = await this.loadClassicFallback(sound, soundPack);
          this.soundCache.set(
            cacheKey,
            fallbackBuffer ?? this.generatePlaceholderSound(sound, soundPack),
          );
        }
      }),
    );
  }

  /**
   * Fetch + decode the classic pack's copy of a sound as a fallback when the
   * active pack is missing the file (bd gw43). Returns null when the request
   * itself is for the classic pack (nothing to fall back to) or when the
   * classic asset also fails — callers then use generatePlaceholderSound.
   */
  private async loadClassicFallback(
    sound: SoundEffect,
    soundPack: SoundPack,
  ): Promise<AudioBuffer | null> {
    if (soundPack === 'classic' || !this.audioContext) return null;
    try {
      const response = await fetch(this.getSoundPath(sound, 'classic'));
      if (!this.isAcceptableAudioResponse(response)) return null;
      const arrayBuffer = await response.arrayBuffer();
      return await this.audioContext.decodeAudioData(arrayBuffer);
    } catch (_error) {
      return null;
    }
  }

  /**
   * Generate placeholder sound based on sound type and theme
   */
  private generatePlaceholderSound(sound: SoundEffect, soundPack: SoundPack): AudioBuffer {
    if (!this.audioContext) throw new Error('AudioContext not initialized');

    const sampleRate = this.audioContext.sampleRate;
    const length = sampleRate * 0.2; // 0.2 second
    const audioBuffer = this.audioContext.createBuffer(1, length, sampleRate);
    const channelData = audioBuffer.getChannelData(0);
    
    // Generate sound based on type and theme
    const { frequency, envelope } = this.getSoundCharacteristics(sound, soundPack);
    
    for (let i = 0; i < length; i++) {
      const t = i / sampleRate;
      const envelopeValue = this.getEnvelopeValue(t, envelope);
      const waveValue = Math.sin(2 * Math.PI * frequency * t) * envelopeValue * 0.3;
      channelData[i] = waveValue;
    }
    
    return audioBuffer;
  }

  /**
   * Get sound characteristics based on type and theme
   */
  private getSoundCharacteristics(sound: SoundEffect, soundPack: SoundPack): { frequency: number; envelope: string } {
    // Frequencies for the procedural-tone fallback. SoundEffect grew over
    // time and not every name has a hand-tuned frequency; aliases and any
    // unmapped variants fall back to a neutral mid-tone via the lookup
    // below.
    const baseFrequencies: Record<SoundEffect, number> = {
      'card-select': 440,
      'card-flip': 523,
      'card-deselect': 330,
      'card-deal': 392,
      'hand-complete': 659,
      'hand-complete-big': 784,
      'hand-complete-royal': 988,
      'streak-start': 880,
      'streak-activate': 880,
      'streak-increase': 1047,
      'streak-decay-warning': 740,
      'streak-break': 196,
      'golden-card-spawn': 1175,
      'golden-card-played': 1397,
      'locked-card-unlock': 587,
      'achievement-unlock': 1319,
      'level-up': 1568,
      'game-start': 220,
      'game-end': 110,
      'button-click': 330,
      'button-hover': 370,
      'error': 185,
      'success': 880,
      'powerup-activate': 1047,
      'powerup-use': 1047,
      'daily-challenge-complete': 1319,
      'timer-warning': 698,
      'invalid': 165,
    };

    const themeModifiers: Record<SoundPack, number> = {
      'classic': 1.0,
      'vegas': 1.2,
      'modern': 0.8,
      'royal': 1.1,
      'ocean': 0.9,
      'sunset': 1.0,
      'midnight': 0.7,
      '8bit': 1.5,
      'western': 0.8,
      'forest': 0.9,
      't800': 1.3,
      'vegas_plus': 1.4,
      'spooky-halloween': 0.6
    };

    return {
      frequency: baseFrequencies[sound] * themeModifiers[soundPack],
      envelope: 'short'
    };
  }

  /**
   * Get envelope value for sound shaping
   */
  private getEnvelopeValue(t: number, envelope: string): number {
    switch (envelope) {
      case 'short':
        return Math.exp(-t * 8);
      case 'medium':
        return Math.exp(-t * 4);
      case 'long':
        return Math.exp(-t * 2);
      default:
        return Math.exp(-t * 6);
    }
  }

  /**
   * Get frequency for different sound types
   */
  private getSoundFrequency(sound: SoundEffect): number {
    switch (sound) {
      case 'card-select': return 440; // A4
      case 'card-flip': return 523; // C5
      case 'hand-complete': return 659; // E5
      case 'button-click': return 330; // E4
      case 'success': return 880; // A5
      case 'error': return 220; // A3
      default: return 440;
    }
  }

  /**
   * Play sound effect
   */
  async playSound(sound: SoundEffect, volume: number = 1): Promise<void> {
    if (!this.isInitialized || !this.settings.sfxEnabled || !this.audioContext) {
      return;
    }

    // Map aliases to actual sound effects
    let actualSound = sound;
    if (sound === 'streak-activate') actualSound = 'streak-start';
    if (sound === 'powerup-use') actualSound = 'powerup-activate';
    // classic/ ships a real timer-warning.mp3 (foley pack, 2026-07); other
    // packs load the classic asset directly via CLASSIC_ONLY_SFX routing in
    // loadSoundPack (no 404 + fallback round-trip anymore).

    try {
      const cacheKey = `${this.currentSoundPack}-${actualSound}`;
      let audioBuffer = this.soundCache.get(cacheKey);
      
      if (!audioBuffer) {
        // Load the sound if not cached
        await this.loadSoundPack(this.currentSoundPack, [actualSound]);
        audioBuffer = this.soundCache.get(cacheKey);
      }

      if (!audioBuffer) {
        return;
      }

      // Create and play sound
      const source = this.audioContext.createBufferSource();
      const gainNode = this.audioContext.createGain();
      
      source.buffer = audioBuffer;
      gainNode.gain.value = volume * this.settings.sfxVolume * this.settings.masterVolume;
      
      source.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      source.start();
    } catch (error) {
      console.warn(`AudioManager: Failed to play sound ${sound}`, error);
    }
  }

  /**
   * Play the correct hand-complete variant based on hand rank + multiplier.
   *
   * Tier selection:
   *   - royal flush / straight flush         → 'hand-complete-royal'
   *   - multiplier ≥ 5× OR hand rank ≥ full  → 'hand-complete-big'
   *   - everything else                      → 'hand-complete'
   *
   * Silently falls back to the base 'hand-complete' if the tier variant
   * isn't cached (so this is safe to call even before the Suno files land).
   */
  async playHandComplete(handName: string, multiplier: number = 1, volume: number = 1): Promise<void> {
    const name = handName.toLowerCase();
    const isRoyal = name.includes('royal flush') || name.includes('straight flush');
    const isBigHand =
      name.includes('full house') ||
      name.includes('four of a kind') ||
      name.includes('flush') ||
      name.includes('straight');

    let tier: SoundEffect = 'hand-complete';
    if (isRoyal) {
      tier = 'hand-complete-royal';
    } else if (multiplier >= 5 || isBigHand) {
      tier = 'hand-complete-big';
    }

    // If the tier variant isn't on disk yet, fall back to the base sound so
    // the game still gets auditory feedback. loadSoundPack already falls back
    // to a synthesized placeholder on 404, so this is belt-and-suspenders.
    const cacheKey = `${this.currentSoundPack}-${tier}`;
    if (!this.soundCache.has(cacheKey)) {
      await this.loadSoundPack(this.currentSoundPack, [tier]);
    }

    await this.playSound(tier, volume);
  }

  /**
   * Play music track
   */
  /**
   * Fetch + decode a single music URL. No caching, no fallback — returns null
   * on any failure stage (fetch, body, decode) with Sentry instrumentation
   * tagging the source ('override' vs 'static') so we can correlate silent
   * music to admin-uploaded files vs bundled files.
   */
  private async fetchAndDecodeMusic(
    track: MusicTrack,
    url: string,
    source: 'override' | 'static' | 'remote',
  ): Promise<AudioBuffer | null> {
    Sentry.addBreadcrumb({
      category: 'audio.music.load',
      level: 'info',
      message: `loadMusicBuffer(${track}) [${source}]`,
      data: {
        track,
        url,
        source,
        contextState: this.audioContext?.state,
        platform: Capacitor.getPlatform(),
      },
    });

    let response: Response;
    try {
      response = await fetch(url);
    } catch (fetchError) {
      // Remote (CDN) fetch failures are EXPECTED whenever a native device is
      // offline — playMusic recovers by dropping to a bundled track
      // (StackedStreak-ns2j). Breadcrumb only: no console spam, no
      // error-level Sentry issue for a fully-recovered condition.
      if (source === 'remote') {
        Sentry.addBreadcrumb({
          category: 'audio.music.fetch',
          level: 'info',
          message: `remote music fetch failed for ${track} — falling back to bundled track`,
          data: { track, url, platform: Capacitor.getPlatform() },
        });
        return null;
      }
      console.warn(`AudioManager: music fetch threw for ${track} [${source}]`, fetchError);
      Sentry.captureException(fetchError, {
        tags: { component: 'AudioManager', stage: 'fetch', track, source },
        extra: { url, platform: Capacitor.getPlatform() },
      });
      return null;
    }
    // status === 0 is the Capacitor iOS WKWebView quirk: WebViewAssetHandler
    // sends a plain URLResponse (no HTTP status) for media file extensions
    // (.mp3/.wav/etc — see node_modules/@capacitor/ios/.../WebViewAssetHandler.swift
    // line 89 `if isMediaExtension(...) urlSchemeTask.didReceive(urlResponse)`).
    // The bytes ARE delivered via the subsequent didReceive(data) call, so
    // arrayBuffer() reads correctly — we just have to skip the response.ok
    // gate (isAcceptableAudioResponse). Without this, every iOS Capacitor
    // build had silent music since we adopted the gapless Web Audio path:
    // fetch resolved with status 0, !response.ok bailed, music never played.
    // This is the bug behind the user-reported "music isn't showing up on
    // the app" report.
    if (!this.isAcceptableAudioResponse(response)) {
      // Same reasoning as the fetch-throw branch above: a bad status from the
      // CDN (offline redirect, 5xx, captive portal) recovers via the bundled
      // fallback track, so keep it at breadcrumb level.
      if (source === 'remote') {
        Sentry.addBreadcrumb({
          category: 'audio.music.fetch',
          level: 'info',
          message: `remote music fetch returned ${response.status} for ${track} — falling back to bundled track`,
          data: { track, url, status: response.status, platform: Capacitor.getPlatform() },
        });
        return null;
      }
      console.warn(`AudioManager: music fetch returned ${response.status} for ${track} [${source}]`);
      // POKERBLITZ-M/Q/N/P/V: status === 0 on web is the browser lifecycle
      // abort (tab backgrounded mid-fetch, user navigated away, network
      // flap). Not an actionable error — drop a breadcrumb and bail.
      if (response.status === 0) {
        Sentry.addBreadcrumb({
          category: 'audio.music.fetch',
          level: 'info',
          message: `music fetch aborted (status 0) for ${track} [${source}]`,
          data: { track, source, platform: Capacitor.getPlatform() },
        });
      } else {
        Sentry.captureMessage(`AudioManager music fetch ${response.status} for ${track} [${source}]`, {
          level: 'warning',
          tags: { component: 'AudioManager', stage: 'fetch', track, source },
          extra: { url, status: response.status, statusText: response.statusText, platform: Capacitor.getPlatform() },
        });
      }
      return null;
    }

    let arrayBuffer: ArrayBuffer;
    try {
      arrayBuffer = await response.arrayBuffer();
    } catch (bodyError) {
      console.warn(`AudioManager: arrayBuffer() failed for ${track} [${source}]`, bodyError);
      Sentry.captureException(bodyError, {
        tags: { component: 'AudioManager', stage: 'arrayBuffer', track, source },
        extra: { url, platform: Capacitor.getPlatform() },
      });
      return null;
    }

    try {
      const audioBuffer = await this.audioContext!.decodeAudioData(arrayBuffer);
      Sentry.addBreadcrumb({
        category: 'audio.music.load',
        level: 'info',
        message: `loadMusicBuffer(${track}) decoded [${source}]`,
        data: { track, source, durationSec: audioBuffer.duration, channels: audioBuffer.numberOfChannels, sampleRate: audioBuffer.sampleRate },
      });
      return audioBuffer;
    } catch (decodeError) {
      console.warn(`AudioManager: decodeAudioData failed for ${track} [${source}]`, decodeError);
      // Remember this track decode-failed so playMusic skips straight to the
      // HTMLAudioElement fallback next time instead of re-fetching + re-decoding.
      this.musicDecodeFailed.add(track);

      // POKERBLITZ-Y / bd drn1: the well-known Mac Catalyst + strict iOS 26.x
      // WebKit case throws `EncodingError: Decoding failed` on the same MP3s
      // the native <audio> decoder plays without complaint. That path is
      // EXPECTED and fully recovered by the HTMLAudioElement fallback in
      // playMusic() (StackedStreak-i0b0) — the user still hears music. Yet we
      // were reporting it via captureException, which created an error-level
      // Sentry issue that fired on every Mac-Catalyst session (19 events /
      // 8 users) and drowned out the genuinely-actionable "music went totally
      // silent" signal. So: downgrade the expected EncodingError to an info
      // breadcrumb (still attached to any later error for context), and only
      // capture an exception for an UNEXPECTED decode failure (a different
      // error name) where we have no evidence the fallback will save us.
      const decodeErrName = (decodeError as { name?: string } | null)?.name ?? '';
      const decodeErrMsg = decodeError instanceof Error ? decodeError.message : String(decodeError);
      const isExpectedWebKitDecodeReject =
        decodeErrName === 'EncodingError' || /decoding failed/i.test(decodeErrMsg);

      const decodeData = {
        track,
        source,
        url,
        errorName: decodeErrName || undefined,
        byteLength: arrayBuffer.byteLength,
        contextState: this.audioContext?.state,
        sampleRate: this.audioContext?.sampleRate,
        platform: Capacitor.getPlatform(),
      };

      if (isExpectedWebKitDecodeReject) {
        Sentry.addBreadcrumb({
          category: 'audio.music.decode',
          level: 'info',
          message: `decodeAudioData EncodingError for ${track} [${source}] — recovering via HTMLAudioElement fallback`,
          data: decodeData,
        });
      } else {
        Sentry.captureException(decodeError, {
          tags: { component: 'AudioManager', stage: 'decode', track, source },
          extra: decodeData,
        });
      }
      return null;
    }
  }

  /**
   * Load a music track as an AudioBuffer (cached). Used by the gapless Web
   * Audio playback path. Returns null if the fetch/decode fails.
   *
   * Tries the admin-uploaded override URL first (if registered for this
   * track), then falls back to the bundled static MP3. Without this fallback,
   * a stale/missing/expired Supabase file in `theme_sound_packs` produced
   * complete silence even though the bundled track was sitting on disk —
   * an admin who uploaded a music file then deleted it from storage would
   * silently kill music for every player on that theme.
   */
  private async loadMusicBuffer(track: MusicTrack): Promise<AudioBuffer | null> {
    if (!this.audioContext) return null;

    // Serve cached
    const cached = this.musicBufferCache.get(track);
    if (cached) return cached;

    // Deduplicate in-flight fetches for the same track
    const inflight = this.musicLoadInFlight.get(track);
    if (inflight) return inflight;

    const overrideUrl = this.musicOverrides.get(track);
    // Web: bundled path. Native: bundled path for menu/classic, remote CDN
    // URL for everything pruned from the binary (StackedStreak-ns2j).
    const resolved = this.resolveMusicUrl(track);

    const loadPromise = (async () => {
      let audioBuffer: AudioBuffer | null = null;

      if (overrideUrl) {
        audioBuffer = await this.fetchAndDecodeMusic(track, overrideUrl, 'override');
        if (!audioBuffer && resolved) {
          // Override failed — drop it so we don't keep retrying a broken URL
          // for the rest of the session. Next theme load can re-register it.
          this.musicOverrides.delete(track);
          Sentry.addBreadcrumb({
            category: 'audio.music.load',
            level: 'warning',
            message: `loadMusicBuffer(${track}) override failed — falling back to ${resolved.source}`,
            data: { track, overrideUrl, fallbackUrl: resolved.url },
          });
        }
      }

      if (!audioBuffer && resolved) {
        audioBuffer = await this.fetchAndDecodeMusic(track, resolved.url, resolved.source);
      }

      this.musicLoadInFlight.delete(track);
      if (audioBuffer) {
        this.musicBufferCache.set(track, audioBuffer);
        this.enforceMusicCacheLimit(track);
      }
      return audioBuffer;
    })();

    this.musicLoadInFlight.set(track, loadPromise);
    return loadPromise;
  }

  /**
   * Play a music track with gapless looping via Web Audio API.
   *
   * AudioBufferSourceNode with loop=true is truly gapless on both iOS WKWebView
   * and every other platform — unlike HTMLAudioElement.loop which has a
   * 200-500ms silent gap on iOS. Tracks also crossfade-in over 120ms to hide
   * the transition between tracks.
   */
  async playMusic(track: MusicTrack, loop: boolean = true): Promise<void> {
    // StackedStreak-g4of: every entry to playMusic gets a breadcrumb so we
    // can see "music was requested but no audio played" in Sentry sessions
    // where the user reports silence.
    Sentry.addBreadcrumb({
      category: 'audio.music.play',
      level: 'info',
      message: `playMusic(${track})`,
      data: {
        track,
        musicEnabled: this.settings.musicEnabled,
        isInitialized: this.isInitialized,
        currentTrack: this.currentTrack,
        contextState: this.audioContext?.state,
        isMusicPaused: this.isMusicPaused,
        platform: Capacitor.getPlatform(),
      },
    });

    if (!this.settings.musicEnabled) return;

    // Don't restart if already playing the same track via either path
    if (this.currentTrack === track && !this.isMusicPaused) {
      if (this.musicSource) return;                                 // gapless still going
      if (this.musicAudio && !this.musicAudio.paused) return;       // fallback still going
    }

    // Ensure AudioManager is initialized before playing music
    if (!this.isInitialized) {
      await this.initialize();
    }
    if (!this.audioContext) {
      Sentry.captureMessage(`AudioManager.playMusic: no audioContext after initialize (${track})`, {
        level: 'warning',
        tags: { component: 'AudioManager', stage: 'no-context', track },
        extra: { platform: Capacitor.getPlatform() },
      });
      return;
    }

    // Resume context if suspended (iOS autoplay policy)
    if (this.audioContext.state === 'suspended') {
      try { await this.audioContext.resume(); } catch (resumeError) {
        Sentry.captureException(resumeError, {
          tags: { component: 'AudioManager', stage: 'resume-in-play', track },
          extra: { platform: Capacitor.getPlatform() },
        });
      }
    }

    // If decodeAudioData has already failed this session for this track, skip
    // the Web Audio path entirely and go straight to the HTMLAudioElement
    // fallback. Re-fetching + re-decoding the same MP3 just to fail the same
    // way again wastes a network roundtrip on every track switch.
    if (this.musicDecodeFailed.has(track)) {
      await this.playMusicFallback(track, loop);
      return;
    }

    // Load the target buffer (cache hit is instant; first play fetches + decodes)
    const buffer = await this.loadMusicBuffer(track);
    if (!buffer) {
      // Native CDN track failed at the network stage (offline / flaky
      // connection — StackedStreak-ns2j): silently drop to a track that IS
      // inside the binary. Decode failures are excluded — there the bytes
      // arrived fine and the HTMLAudioElement fallback below can play them.
      // No recursion risk: the offline fallback track is always in
      // NATIVE_BUNDLED_MUSIC, so its own load never takes the remote path.
      const resolved = this.resolveMusicUrl(track);
      if (resolved?.source === 'remote' && !this.musicDecodeFailed.has(track)) {
        const offlineFallback = this.getOfflineFallbackTrack(track);
        if (offlineFallback !== track) {
          Sentry.addBreadcrumb({
            category: 'audio.music.play',
            level: 'info',
            message: `playMusic(${track}) remote track unavailable — playing bundled ${offlineFallback}`,
            data: { track, offlineFallback, platform: Capacitor.getPlatform() },
          });
          await this.playMusic(offlineFallback, loop);
          return;
        }
      }
      // Web Audio failed (decode reject is the common case on Mac Catalyst /
      // strict WebKit). Try the HTMLAudioElement fallback — the same MP3 played
      // by the native <audio> decoder, which is far more permissive. Loses the
      // gapless loop (200-500ms gap on iOS) but beats silence.
      const fallbackPlayed = await this.playMusicFallback(track, loop);
      if (!fallbackPlayed) {
        // BOTH the gapless Web Audio path AND the HTMLAudioElement fallback
        // failed — this is the genuinely-actionable case (the user actually
        // hears silence), unlike the benign decode→fallback path which we
        // deliberately downgraded to a breadcrumb above. Capture it so it
        // surfaces in Sentry in place of the old expected-EncodingError noise
        // (bd drn1 / POKERBLITZ-Y).
        Sentry.captureMessage(
          `AudioManager.playMusic: silent — Web Audio AND HTMLAudioElement fallback both failed (${track})`,
          {
            level: 'warning',
            tags: { component: 'AudioManager', stage: 'silent', track },
            extra: {
              decodeFailed: this.musicDecodeFailed.has(track),
              contextState: this.audioContext?.state,
              platform: Capacitor.getPlatform(),
            },
          },
        );
      }
      return;
    }

    // Stop any currently playing music before starting the new source
    this.stopMusic();

    try {
      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;
      source.loop = loop;

      const gain = this.audioContext.createGain();
      const targetVolume = this.settings.musicVolume * this.settings.masterVolume;
      // Crossfade in over 120ms — masks the "start" click and feels gentler
      gain.gain.setValueAtTime(0, this.audioContext.currentTime);
      gain.gain.linearRampToValueAtTime(targetVolume, this.audioContext.currentTime + 0.12);

      source.connect(gain);
      gain.connect(this.audioContext.destination);
      source.start(0);

      this.musicSource = source;
      this.musicGain = gain;
      this.currentTrack = track;
      this.isMusicPaused = false;
      this.pausedTrack = null;

      Sentry.addBreadcrumb({
        category: 'audio.music.play',
        level: 'info',
        message: `playMusic(${track}) started`,
        data: { targetVolume, contextState: this.audioContext.state, contextTime: this.audioContext.currentTime },
      });
    } catch (error) {
      console.warn(`AudioManager: Failed to play music ${track}`, error);
      Sentry.captureException(error, {
        tags: { component: 'AudioManager', stage: 'start', track },
        extra: {
          contextState: this.audioContext?.state,
          bufferDurationSec: buffer.duration,
          platform: Capacitor.getPlatform(),
        },
      });
    }
  }

  /**
   * Fallback music path using HTMLAudioElement when the gapless Web Audio
   * decoder rejects the bundled MP3 (StackedStreak-i0b0 — Mac Catalyst /
   * iOS 26.3 WebKit's strict decodeAudioData throws EncodingError on files
   * the native <audio> decoder plays without complaint).
   *
   * Returns true if playback started, false if the fallback also failed.
   * Loses gapless looping (HTMLAudioElement.loop has a 200-500ms gap on iOS
   * WKWebView), but that's the original behavior every release before
   * eef05128 shipped with — and it beats silence.
   */
  private async playMusicFallback(track: MusicTrack, loop: boolean): Promise<boolean> {
    // Same URL resolution as the Web Audio path: on native, non-bundled
    // tracks live on the CDN, not in the binary (StackedStreak-ns2j).
    const url = this.musicOverrides.get(track) || this.resolveMusicUrl(track)?.url;
    if (!url) return false;

    Sentry.addBreadcrumb({
      category: 'audio.music.play',
      level: 'warning',
      message: `playMusic(${track}) using HTMLAudioElement fallback`,
      data: { track, url, platform: Capacitor.getPlatform() },
    });

    // Stop any currently playing audio (Web Audio source AND any prior <audio>).
    // Also clears any in-flight fallback volume fade (clearMusicFade inside
    // stopMusic) so an old fade interval can't ramp the new element's volume.
    this.stopMusic();

    // Assign musicAudio + currentTrack BEFORE awaiting play() so any concurrent
    // stopMusic() / playMusic() call during the play promise can find this
    // element and pause it. Without this, the original implementation leaked
    // orphan <audio> elements: when game-end fired stopMusic() while
    // audio.play() was still in flight, this.musicAudio was still null, the
    // stop missed, and the audio kept playing forever (game-music-after-exit
    // bug reported on TestFlight 1.8.6 build 130).
    const audio = new Audio(url);
    audio.loop = loop;
    audio.volume = this.settings.musicVolume * this.settings.masterVolume;
    this.musicAudio = audio;
    this.currentTrack = track;
    this.isMusicPaused = false;
    this.pausedTrack = null;

    try {
      const playPromise = audio.play();
      if (playPromise) await playPromise;

      // If some other code path replaced or cleared musicAudio while we were
      // awaiting, this element is now an orphan — stop it explicitly so we
      // don't leak a playing <audio> the manager no longer tracks.
      if (this.musicAudio !== audio) {
        try { audio.pause(); } catch {}
        try { audio.currentTime = 0; } catch {}
        Sentry.addBreadcrumb({
          category: 'audio.music.play',
          level: 'warning',
          message: `playMusic(${track}) fallback orphaned during await — stopped`,
          data: { url, platform: Capacitor.getPlatform() },
        });
        return false;
      }

      Sentry.addBreadcrumb({
        category: 'audio.music.play',
        level: 'info',
        message: `playMusic(${track}) started via fallback`,
        data: { url, volume: audio.volume, platform: Capacitor.getPlatform() },
      });
      return true;
    } catch (error) {
      console.warn(`AudioManager: HTMLAudioElement fallback failed for ${track}`, error);
      // Roll back the optimistic assignment if no one else replaced it
      if (this.musicAudio === audio) {
        try { audio.pause(); } catch {}
        this.musicAudio = null;
        this.currentTrack = null;
      }
      Sentry.captureException(error, {
        tags: { component: 'AudioManager', stage: 'fallback-play', track },
        extra: { url, platform: Capacitor.getPlatform() },
      });
      return false;
    }
  }

  /**
   * Play theme-specific music
   */
  async playThemeMusic(themeId: string, loop: boolean = true): Promise<void> {
    // Map theme IDs to music track names
    const themeToTrackMap: Record<string, MusicTrack> = {
      'classic': 'classic-casino',
      'vegas': 'vegas-nights',
      'modern': 'modern-dark',
      'royal': 'royal-gold',
      'ocean': 'ocean-blue',
      'sunset': 'sunset-glow',
      'midnight': 'midnight-sky',
      '8bit': '8bit-retro',
      'western': 'wild-west-saloon',
      'forest': 'forest-zen',
      't800': 'robots-are-coming',
      'vegas_plus': 'vegas-plus-neon-palace',
      'spooky-halloween': 'spooky-halloween'
    };

    const trackName = themeToTrackMap[themeId];
    if (trackName) {
      await this.playMusic(trackName, loop);
    } else {
      console.warn(`[AudioManager] No music track mapped for theme '${themeId}' — falling back to silence`);
    }
  }

  /**
   * Stop current music
   */
  stopMusic(): void {
    // Cancel any in-flight fallback volume fade so it can't fight a
    // subsequent playMusicFallback()'s volume assignment.
    this.clearMusicFade();
    // Stop & disconnect Web Audio source
    if (this.musicSource) {
      try { this.musicSource.stop(0); } catch {}
      try { this.musicSource.disconnect(); } catch {}
      this.musicSource = null;
    }
    if (this.musicGain) {
      try { this.musicGain.disconnect(); } catch {}
      this.musicGain = null;
    }
    // Defensive: also stop legacy HTMLAudio if anything ever set it
    if (this.musicAudio) {
      this.musicAudio.pause();
      this.musicAudio.currentTime = 0;
      this.musicAudio = null;
    }
    this.currentTrack = null;
    this.isMusicPaused = false;
    this.pausedTrack = null;
  }

  /**
   * Pause current music without losing position.
   *
   * AudioBufferSourceNode can't be paused natively — once stopped it can't
   * restart — so we simulate pause by suspending the AudioContext. This
   * matches iOS/Android behaviour where Capacitor can pause the whole app's
   * audio session when backgrounded.
   */
  pauseMusic(): void {
    if (this.isMusicPaused) return;

    // HTMLAudioElement fallback path: pause the <audio> element directly.
    // Don't suspend the AudioContext — it powers SFX too.
    if (this.musicAudio && !this.musicAudio.paused) {
      this.pausedTrack = this.currentTrack;
      this.isMusicPaused = true;
      try { this.musicAudio.pause(); } catch {}
      console.log('AudioManager: Music paused (fallback)');
      return;
    }

    // Web Audio path
    if (!this.musicSource || !this.audioContext) return;
    this.pausedTrack = this.currentTrack;
    this.isMusicPaused = true;
    try { this.audioContext.suspend(); } catch {}
    console.log('AudioManager: Music paused');
  }

  /**
   * Resume current music from paused state.
   *
   * Resumes the AudioContext whenever it's suspended — not just when WE
   * paused it. The browser (hidden tab throttling) and iOS (AVAudioSession
   * interruption from a phone call / Siri / alarm) can both suspend the
   * context behind our back. Without this, audio stays dead until the
   * next cold launch.
   */
  resumeMusic(): void {
    if (!this.settings.musicEnabled) return;

    // HTMLAudioElement fallback path: resume the <audio> element. Don't touch
    // AudioContext here — it may belong to a separate SFX-only path.
    if (this.musicAudio && this.musicAudio.paused) {
      this.musicAudio.play().catch((err) => {
        Sentry.captureException(err, {
          tags: { component: 'AudioManager', stage: 'fallback-resume', track: this.currentTrack ?? 'unknown' },
          extra: { platform: Capacitor.getPlatform() },
        });
      });
      this.isMusicPaused = false;
      console.log('AudioManager: Music resumed (fallback)');
      return;
    }

    if (this.audioContext && this.audioContext.state === 'suspended') {
      try { this.audioContext.resume(); } catch {}
    }

    if (this.isMusicPaused) {
      this.isMusicPaused = false;
      console.log('AudioManager: Music resumed');
    } else if (!this.musicSource && this.pausedTrack) {
      // Context dropped the source entirely — start fresh
      this.playMusic(this.pausedTrack).catch(() => {});
    }
  }

  /**
   * Fade music volume over `duration` ms. Uses Web Audio linear ramp for
   * smooth, click-free transitions.
   */
  fadeMusic(targetVolume: number, duration: number = 1000): void {
    // HTMLAudioElement fallback path (Mac Catalyst / strict iOS 26.x WebKit
    // where decodeAudioData rejects): there is no GainNode, so ramp the
    // element's volume manually. Without this branch the musicGain
    // early-return below made game-end fades a silent no-op on fallback
    // devices — the handlers call fadeMusic(0, 600) then stopMusic() after
    // 650ms, so music was cut dead mid-bar instead of fading out.
    if (this.musicAudio && !this.musicAudio.paused) {
      this.clearMusicFade();
      const audio = this.musicAudio;
      const startVolume = audio.volume;
      const target = Math.min(1, Math.max(0, targetVolume));
      const startTime = Date.now();
      this.musicFadeInterval = setInterval(() => {
        // Bail if the element we were fading is no longer the active one
        // (stopMusic/playMusicFallback also clear the handle, this is
        // belt-and-suspenders against a stale closure).
        if (this.musicAudio !== audio) {
          this.clearMusicFade();
          return;
        }
        const progress = duration > 0 ? Math.min((Date.now() - startTime) / duration, 1) : 1;
        const next = startVolume + (target - startVolume) * progress;
        try { audio.volume = Math.min(1, Math.max(0, next)); } catch {}
        if (progress >= 1) {
          this.clearMusicFade();
        }
      }, 25);
      return;
    }

    if (!this.musicGain || !this.audioContext) return;
    const now = this.audioContext.currentTime;
    const current = this.musicGain.gain.value;
    this.musicGain.gain.cancelScheduledValues(now);
    this.musicGain.gain.setValueAtTime(current, now);
    this.musicGain.gain.linearRampToValueAtTime(targetVolume, now + duration / 1000);
  }

  /**
   * Clear any in-flight HTMLAudioElement volume fade (see fadeMusic).
   */
  private clearMusicFade(): void {
    if (this.musicFadeInterval !== null) {
      clearInterval(this.musicFadeInterval);
      this.musicFadeInterval = null;
    }
  }

  /**
   * Update settings
   */
  updateSettings(newSettings: Partial<AudioSettings>): void {
    const previousMusicEnabled = this.settings.musicEnabled;
    this.settings = { ...this.settings, ...newSettings };
    this.saveSettings();

    // Stop music if disabled (covers both Web Audio and HTMLAudioElement paths)
    if (previousMusicEnabled && !this.settings.musicEnabled && (this.musicSource || this.musicAudio)) {
      this.stopMusic();
    }

    const target = this.settings.musicVolume * this.settings.masterVolume;

    // Update music volume on the Web Audio gain node (click-free)
    if (this.musicGain && this.audioContext) {
      this.musicGain.gain.setValueAtTime(target, this.audioContext.currentTime);
    }
    // Update music volume on the HTMLAudioElement fallback
    if (this.musicAudio) {
      this.musicAudio.volume = target;
    }

    // Notify subscribers (useAudioSettings) so every settings UI re-syncs.
    // Detail shape is { settings } — deliberately NOT { musicEnabled } so the
    // page.tsx music-restart listener (which keys on detail.musicEnabled)
    // only fires for restartMusicIfEnabled's dispatch below, not on every
    // volume-slider drag.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('audioSettingsChanged', {
        detail: { settings: this.getSettings() }
      }));
    }
  }

  /**
   * Restart music if enabled and not currently playing
   */
  restartMusicIfEnabled(): void {
    const fallbackPlaying = this.musicAudio && !this.musicAudio.paused;
    if (this.settings.musicEnabled && !this.musicSource && !fallbackPlaying) {
      // Dispatch event to trigger music restart based on current game state
      window.dispatchEvent(new CustomEvent('audioSettingsChanged', {
        detail: { musicEnabled: this.settings.musicEnabled }
      }));
    }
  }

  /**
   * Get current settings
   */
  getSettings(): AudioSettings {
    return { ...this.settings };
  }

  /**
   * Get current music track
   */
  getCurrentTrack(): MusicTrack | null {
    return this.currentTrack;
  }

  /**
   * Check if audio is enabled
   */
  isAudioEnabled(): boolean {
    return this.settings.musicEnabled || this.settings.sfxEnabled;
  }

  /**
   * Switch to a different sound pack
   */
  async switchSoundPack(themeId: string): Promise<void> {
    const soundPack = this.themeToSoundPack[themeId] || 'classic';

    if (soundPack === this.currentSoundPack) {
      return; // Already using this sound pack
    }

    // Clear cache for the old sound pack to force reload
    this.clearSoundPackCache(this.currentSoundPack);

    this.currentSoundPack = soundPack;

    // Preload essential sounds for the new pack
    const essentialSounds: SoundEffect[] = [
      'card-select',
      'card-flip',
      'hand-complete',
      'button-click',
      'success',
      'error'
    ];

    await this.loadSoundPack(soundPack, essentialSounds);
  }

  /**
   * Clear cache for a specific sound pack
   */
  private clearSoundPackCache(soundPack: SoundPack): void {
    const keysToDelete = [];
    for (const key of this.soundCache.keys()) {
      if (key.startsWith(`${soundPack}-`)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => this.soundCache.delete(key));
    // Overrides are bound to a specific theme; clear them too so the next
    // theme gets a clean slate (ThemeContext will push new overrides after
    // calling switchSoundPack).
    this.soundOverrides.clear();
  }

  /**
   * Bound musicBufferCache to MUSIC_CACHE_MAX entries (perf brief CODE-08 /
   * bd dmpv). Called after each successful loadMusicBuffer insert. Evicts
   * the oldest non-current entry (Map iteration preserves insertion order).
   * Never evicts the entry matching `this.currentTrack` because that's
   * actively playing — otherwise a theme-switch storm could yank the
   * currently-playing buffer from under playMusic().
   */
  private enforceMusicCacheLimit(justInserted: MusicTrack): void {
    while (this.musicBufferCache.size > AudioManager.MUSIC_CACHE_MAX) {
      let evicted = false;
      for (const key of this.musicBufferCache.keys()) {
        if (key === this.currentTrack) continue;
        if (key === justInserted) continue;
        this.musicBufferCache.delete(key);
        evicted = true;
        break;
      }
      if (!evicted) {
        // Couldn't find a safe eviction candidate (all entries are pinned).
        // Bail rather than touch the playing/just-inserted buffer.
        break;
      }
    }
  }

  /**
   * Get current sound pack
   */
  getCurrentSoundPack(): SoundPack {
    return this.currentSoundPack;
  }

  /**
   * Get the appropriate menu track based on current date/season and user preference
   */
  getMenuTrack(): MusicTrack {
    const now = new Date();
    const month = now.getMonth() + 1; // 1-12
    const day = now.getDate();

    // Halloween period: Oct 29-31
    const isHalloweenPeriod = month === 10 && day >= 29 && day <= 31;

    // Only play Halloween menu music if:
    // 1. It's Halloween period AND
    // 2. User is actually using the Halloween theme (hasn't opted out)
    if (isHalloweenPeriod) {
      try {
        const currentTheme = localStorage.getItem('selectedTheme');
        return currentTheme === 'spooky-halloween' ? 'halloween-menu' : 'menu';
      } catch {
        return 'menu';
      }
    }

    return 'menu';
  }

  /**
   * Preload all sounds for a theme (for smooth switching)
   */
  async preloadThemeSounds(themeId: string): Promise<void> {
    const soundPack = this.themeToSoundPack[themeId] || 'classic';
    await this.loadSoundPack(soundPack);
  }

  /**
   * Contextual music based on game state and current theme
   */
  updateMusicForGameState(gameState: {
    isPlaying: boolean;
    streak: number;
    timeRemaining: number;
    isIntense: boolean;
    currentThemeId?: string;
  }): void {
    if (!this.settings.musicEnabled) return;

    let targetTrack: MusicTrack;

    if (!gameState.isPlaying) {
      targetTrack = this.getMenuTrack();
    } else if (gameState.currentThemeId) {
      // Map theme ID to music track
      const themeToTrackMap: Record<string, MusicTrack> = {
        'classic': 'classic-casino',
        'vegas': 'vegas-nights',
        'modern': 'modern-dark',
        'royal': 'royal-gold',
        'ocean': 'ocean-blue',
        'sunset': 'sunset-glow',
        'midnight': 'midnight-sky',
        '8bit': '8bit-retro',
        'western': 'wild-west-saloon',
        'forest': 'forest-zen',
        't800': 'robots-are-coming',
        'vegas_plus': 'vegas-plus-neon-palace',
        'spooky-halloween': 'spooky-halloween'
      };
      
      targetTrack = themeToTrackMap[gameState.currentThemeId] || 'classic-casino';
    } else {
      // Fallback to classic casino theme if no theme specified
      targetTrack = 'classic-casino';
    }

    if (this.currentTrack !== targetTrack) {
      this.playMusic(targetTrack);
    }
  }

}

// Export singleton instance
export const audioManager = new AudioManager();

// Expose the singleton on window only in dev OR when explicitly running
// the smoke suite (perf brief CODE-09 / bd dmpv). Pre-fix this attached
// unconditionally including in shipped production builds, keeping a
// global reference to the audioManager closures that the GC could never
// reclaim. Smoke tests + Chrome-MCP probes still get their access via
// VITE_SMOKE_TEST=true; real users now lose this dead pin.
if (
  typeof window !== 'undefined' &&
  (import.meta.env.DEV || import.meta.env.VITE_SMOKE_TEST === 'true')
) {
  (window as unknown as { audioManager: typeof audioManager }).audioManager = audioManager;
}